// The fit rule (docs/long-names-code-todo.md, "Fit to the visible screen",
// S3 / S4, and "Phone landscape"). Figma: the `Spades · Table height`
// collection (Full 404 / Short 360) and Long names section 12 (tiny phones).
// Pure functions only; useScreenFit wires them to the browser.
//
// Tablet and desktop (chat 14, T1 / D1) run the same rule with a bigger cap:
// the stage is the 390 design zoomed by s, centred in its column.

/** `table/oval-height` Full. */
export const TABLE_HEIGHT_FULL = 404;
/** `table/oval-height` Short. */
export const TABLE_HEIGHT_SHORT = 360;
/** Home's title band + gap + buttons: everything on Home but the table. */
export const HOME_CHROME = 187;
/** Space kept below the bottom button. */
export const BOTTOM_GAP = 16;
/** The Table board around the oval: the score band and its gap (120) and
 * Team made's overhang below the rim (36). */
export const BOARD_CHROME = 156;
/** The latest past round, collapsed, which phones and tablets keep on
 * screen under the board (2026-10-22). */
export const LATEST_ROUND = 68;
/**
 * Phones: everything on the board but the oval, down to the latest past
 * round, all at the stage's scale. It's more than Home needs (187 + 16), so
 * Home fits whenever the board does.
 */
export const PHONE_BOARD_CHROME = BOARD_CHROME + LATEST_ROUND;
/** Phones stop shrinking here; anything shorter scrolls. */
export const MIN_PHONE_SCALE = 0.85;
/** The width the Figma screens are drawn at. */
export const DESIGN_WIDTH = 390;
/** The board and Home step out of .App's 8pt padding on each side. */
export const PAGE_BLEED = 8;
/** A sideways phone, not merely a short window (tablets are far taller). */
export const PHONE_LANDSCAPE_QUERY =
  '(orientation: landscape) and (max-height: 500px)';

/** Breakpoints (audit C17): phone < 600, tablet 600-1023, desktop >= 1024. */
export type ScreenLayout = 'phone' | 'tablet' | 'desktop';
export const TABLET_MIN_WIDTH = 600;
export const DESKTOP_MIN_WIDTH = 1024;
export const TABLET_QUERY = `(min-width: ${TABLET_MIN_WIDTH}px)`;
export const DESKTOP_QUERY = `(min-width: ${DESKTOP_MIN_WIDTH}px)`;
/** T1: the board and Home x1.6 (624pt on an 834 iPad). */
export const TABLET_SCALE = 1.6;
/** D1: the board and Home x1.42 (553.8pt). */
export const DESKTOP_SCALE = 1.42;
/** Desktop right rail (history or Game setup): 460 wide, 24 from the edge. */
export const RAIL_WIDTH = 460;
export const RAIL_MARGIN = 24;
/** The rail's content is the 390 phone design x1.18. */
export const RAIL_ZOOM = 1.18;
/** Tablet rounds under the table: x1.25 at the stage's width. */
export const TABLET_HISTORY_ZOOM = 1.25;
/** The phone header x1.2 on tablets; Header · wide x1.12 on desktop. */
export const TABLET_HEADER_ZOOM = 1.2;
export const DESKTOP_HEADER_ZOOM = 1.12;
/** Space kept either side of a tablet or desktop stage. */
const STAGE_GUTTER = 16;

/** Viewport shrinkage that counts as an on-screen keyboard, not a toolbar. */
const KEYBOARD_MIN_HEIGHT = 150;

export interface ScreenFit {
  /** The oval's height in px: 360 to 404. */
  tableHeight: number;
  /** The screen's scale below the header: at most 1. */
  scale: number;
}

export const FULL_FIT: ScreenFit = {
  tableHeight: TABLE_HEIGHT_FULL,
  scale: 1,
};

const clamp = (min: number, value: number, max: number) =>
  Math.min(max, Math.max(min, value));

/**
 * Phones and tablets keep the header, the board and its latest past round on
 * screen. The board is (156 + H) x s tall, plus that round: 68 x s on
 * phones, and on tablets 68 x min(1.25, s), since TabletHistory holds the
 * rounds at x1.25 whatever the stage's scale.
 * 1. The table gives up height first, at the cap: H = clamp(360, ..., 404).
 * 2. Then the screen scales, up to the cap; phones stop at x0.85.
 * Desktop's rounds are in the rail, so it keeps Home's budget, with a 16pt
 * gap and no floor: H = clamp(360, (visible - top - 16) / cap - 187, 404).
 * A sideways phone skips both (H = 404, s = 1) and scrolls instead.
 */
export function fitScreen({
  visible,
  top,
  isPhoneLandscape = false,
  maxScale = 1,
  layout = 'phone',
}: {
  visible: number;
  top: number;
  isPhoneLandscape?: boolean;
  /** The layout's scale (stageCap): 1 on phones, x1.6 / x1.42 wider up. */
  maxScale?: number;
  layout?: ScreenLayout;
}): ScreenFit {
  if (isPhoneLandscape || !(visible > 0)) return FULL_FIT;
  // Whole points, like Figma's modes (1440x900 gives Desktop 373).
  const fitTable = (points: number) =>
    clamp(TABLE_HEIGHT_SHORT, Math.floor(points), TABLE_HEIGHT_FULL);

  if (layout === 'desktop') {
    const room = visible - top - BOTTOM_GAP;
    const tableHeight = fitTable(room / maxScale - HOME_CHROME);
    const scale = Math.min(maxScale, room / (HOME_CHROME + tableHeight));
    return { tableHeight, scale };
  }

  const room = visible - top;
  // The latest round's own zoom: x1.25 on tablets, unless the stage is less.
  const roundZoom = layout === 'tablet' ? TABLET_HISTORY_ZOOM : Infinity;
  const round = (scale: number) => LATEST_ROUND * Math.min(roundZoom, scale);
  const tableHeight = fitTable(
    (room - round(maxScale)) / maxScale - BOARD_CHROME,
  );
  // With the round at the stage's scale; on a tablet above x1.25 the round
  // stops growing, which leaves the stage more room.
  let scale = room / (PHONE_BOARD_CHROME + tableHeight);
  if (scale > roundZoom)
    scale = (room - round(roundZoom)) / (BOARD_CHROME + tableHeight);
  scale = Math.min(maxScale, scale);
  return {
    tableHeight,
    scale: layout === 'phone' ? Math.max(MIN_PHONE_SCALE, scale) : scale,
  };
}

/** Which layout a window gets. A sideways phone keeps the phone layout. */
export function screenLayout({
  width,
  isPhoneLandscape = false,
}: {
  width: number;
  isPhoneLandscape?: boolean;
}): ScreenLayout {
  if (isPhoneLandscape) return 'phone';
  if (width >= DESKTOP_MIN_WIDTH) return 'desktop';
  if (width >= TABLET_MIN_WIDTH) return 'tablet';
  return 'phone';
}

/**
 * The most the stage may scale: T1 x1.6 / D1 x1.42, but never wider than its
 * column less 16pt gutters (at 600-655 the x1.6 board would overflow, and a
 * 1024 desktop's column is only 540 next to the rail).
 */
export function stageCap({
  layout,
  width,
}: {
  layout: ScreenLayout;
  width: number;
}): number {
  if (layout === 'phone') return 1;
  const column =
    layout === 'desktop' ? width - RAIL_WIDTH - RAIL_MARGIN : width;
  const cap = layout === 'desktop' ? DESKTOP_SCALE : TABLET_SCALE;
  return Math.min(cap, (column - 2 * STAGE_GUTTER) / DESIGN_WIDTH);
}

/**
 * The visible height: a `100svh` probe (toolbars showing; the keyboard
 * doesn't change it), else `visualViewport.height`, else `innerHeight`.
 * Never `100vh`, which ignores iPhone toolbars.
 */
export function readVisibleHeight({
  svh,
  visualViewport,
  innerHeight,
}: {
  svh: number;
  visualViewport: number | undefined;
  innerHeight: number;
}): number {
  if (svh > 0) return svh;
  if (visualViewport && visualViewport > 0) return visualViewport;
  return innerHeight;
}

/**
 * The on-screen keyboard shrinks the visual viewport but not the layout
 * viewport (iOS Safari, and Android Chrome's default resizes-visual).
 * Pinch zoom shrinks it too, so a zoomed viewport never counts.
 */
export function isKeyboardOpen({
  innerHeight,
  viewportHeight,
  viewportScale = 1,
}: {
  innerHeight: number;
  viewportHeight: number | undefined;
  viewportScale?: number;
}): boolean {
  if (viewportHeight == null) return false;
  if (Math.abs(viewportScale - 1) > 0.01) return false;
  return innerHeight - viewportHeight > KEYBOARD_MIN_HEIGHT;
}

export interface StageLayout {
  /** The content's layout width before zoom. */
  width: number;
  zoom: number;
  /** Left margin in the stage's own (zoomed) px. */
  marginLeft: number;
}

/**
 * How the stage under the header is laid out, or null to leave the page as
 * it is. Scaled: the content keeps the 390 design width (or the phone's own,
 * if wider) and is centred, as in Figma section 12 (×0.9 → 351 wide at x 12).
 * Phone landscape: the portrait layout at ×1, centred.
 *
 * `width` is the screen width the page would span (the .App box), and
 * `available` the .App content box the stage sits in.
 */
export function stageLayout({
  width,
  available,
  scale,
  isPhoneLandscape = false,
  layout = 'phone',
}: {
  width: number;
  available: number;
  scale: number;
  isPhoneLandscape?: boolean;
  layout?: ScreenLayout;
}): StageLayout | null {
  let contentWidth: number;
  if (layout !== 'phone') {
    // Tablet and desktop: the 390 design, zoomed and centred.
    contentWidth = DESIGN_WIDTH;
  } else if (isPhoneLandscape) {
    contentWidth = Math.min(width, DESIGN_WIDTH);
  } else if (scale < 1) {
    contentWidth = Math.min(width / scale, Math.max(width, DESIGN_WIDTH));
  } else {
    return null;
  }
  // The stage pads by the bleed and the content steps back out of it, so
  // the stage's own box is the content width.
  const outer = contentWidth * scale;
  return {
    width: contentWidth,
    zoom: scale,
    marginLeft: (available - outer) / 2 / scale,
  };
}
